import React from 'react';
import Chart from 'react-apexcharts';
import { Empty } from 'antd';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { Studio2Player, Studio2BiasFinding } from '../../live/types';
import BandChip from '../BandChip';
import KpiTile from '../KpiTile';
import { bandColor } from '../../helpers';

/* Slug → display label (kept in sync with reports.constants BIAS_TYPES). */
const BIAS_LABEL: Record<string, string> = {
  disposition_effect: 'Disposition effect',
  loss_aversion: 'Loss aversion',
  overconfidence: 'Overconfidence',
  anchoring: 'Anchoring',
  confirmation_bias: 'Confirmation bias',
  recency: 'Recency',
  herding: 'Herding',
  revenge_trading: 'Revenge trading',
  adaptive_conviction: 'Adaptive conviction',
  calibration: 'Calibration',
  cross_asset_coherence: 'Cross-asset coherence',
  process_over_outcome: 'Process over outcome',
};
const labelOf = (slug: string): string =>
  BIAS_LABEL[slug] ?? slug.replace(/_/g, ' ');

const sevInt = (v: number | null) => (v == null ? '—' : `${Math.round(v)}`);
const confPct = (v: number | null) =>
  v == null ? '—' : `${Math.round(v * 100)}%`;

const EvidenceList: React.FC<{ items: Studio2BiasFinding['evidence'] }> = ({
  items,
}) => {
  if (!items.length) return null;
  return (
    <div className="studio2__evidence">
      {items.map((e, i) => (
        <div className="studio2__evidence-row" key={i}>
          <span className="studio2__evidence-meta">
            {[e.scenario_date, e.asset, e.direction]
              .filter(Boolean)
              .join(' · ')}
          </span>
          {e.note && <span className="studio2__evidence-note">{e.note}</span>}
        </div>
      ))}
    </div>
  );
};

const BiasCard: React.FC<{ f: Studio2BiasFinding }> = ({ f }) => (
  <div className="studio2__card">
    <div className="studio2__card-head">
      <span className="studio2__card-title">{labelOf(f.bias_type)}</span>
      <BandChip band={f.band} title="Concern level (0 none → 3 serious)" />
    </div>
    <div className="studio2__card-body">
      <div className="studio2__bias-metrics">
        <span>
          severity{' '}
          <strong style={{ color: bandColor(f.band) }}>
            {sevInt(f.severity)}
          </strong>
        </span>
        <span>
          confidence <strong>{confPct(f.confidence)}</strong>
        </span>
      </div>
      <div className="studio2__bias-sevbar">
        <div
          className="studio2__bias-sevbar-fill"
          style={{
            width: `${Math.max(0, Math.min(100, f.severity ?? 0))}%`,
            background: bandColor(f.band),
          }}
        />
      </div>
      <p className="studio2__bias-summary">{f.summary}</p>
      <EvidenceList items={f.evidence} />
    </div>
  </div>
);

const BiasPanel: React.FC<{ player: Studio2Player }> = ({ player }) => {
  const bias = player.bias;

  if (!bias || bias.status === 0) {
    return (
      <div className="studio2__bias studio2__bias--empty">
        <Empty
          description={
            bias && bias.status === 0
              ? 'Generating bias analysis…'
              : 'No bias analysis yet for this player.'
          }
        />
      </div>
    );
  }
  if (bias.status === 2) {
    return (
      <div className="studio2__bias studio2__bias--empty">
        <Empty description="Bias analysis failed — regenerate to retry." />
      </div>
    );
  }

  const biases = bias.findings.filter(f => f.category === 'bias');
  const signatures = bias.findings.filter(f => f.category === 'signature');

  const radarOptions: any = {
    chart: {
      type: 'radar',
      toolbar: { show: false },
      animations: { enabled: false },
      parentHeightOffset: 0,
      offsetY: 0,
    },
    // Enlarge the polygon itself (apex leaves a generous default margin) so it
    // fills the card vertically instead of floating in the middle.
    plotOptions: { radar: { size: 140 } },
    xaxis: {
      categories: biases.map(f => labelOf(f.bias_type)),
      labels: { style: { fontSize: '11px' } },
    },
    yaxis: { show: false, min: 0, max: 100 },
    fill: { opacity: 0.22 },
    stroke: { width: 2 },
    markers: { size: 4 },
    colors: ['#d14343'],
    dataLabels: { enabled: false },
    tooltip: { y: { formatter: (v: number) => `${Math.round(v)} / 100` } },
  };
  const radarSeries = [
    { name: 'Severity', data: biases.map(f => f.severity ?? 0) },
  ];

  return (
    <div className="studio2__bias">
      <div className="studio2__bias-top">
        <div className="studio2__card">
          <div className="studio2__card-head">
            <span className="studio2__card-title">Bias profile</span>
          </div>
          <div className="studio2__card-body studio2__card-body--pad0 studio2__bias-radar">
            <Chart
              type="radar"
              options={radarOptions}
              series={radarSeries}
              height={340}
            />
          </div>
        </div>

        <div className="studio2__card">
          <div className="studio2__card-head">
            <span className="studio2__card-title">Overall & signatures</span>
            <BandChip
              band={bias.overall_band}
              title="Overall behavioral-discipline concern"
            />
          </div>
          <div className="studio2__card-body">
            <div className="studio2__sig-tiles">
              {signatures.map(s => (
                <KpiTile
                  key={s.id}
                  label={labelOf(s.bias_type)}
                  value={sevInt(s.severity)}
                  tooltip={s.summary}
                  sub={
                    <BandChip
                      band={s.band}
                      title="Concern (weak competence → higher)"
                    />
                  }
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="studio2__bias-cards">
        {biases.map(f => (
          <BiasCard key={f.id} f={f} />
        ))}
      </div>

      <div className="studio2__card">
        <div className="studio2__card-head">
          <span className="studio2__card-title">Analyst narrative</span>
        </div>
        <div className="studio2__card-body studio2__card-body--scroll">
          <div className="studio2__markdown">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {bias.narrative ?? ''}
            </ReactMarkdown>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BiasPanel;
